import React from "react";
import { ClockCircleOutlined, HourglassOutlined } from "@ant-design/icons";

export default function TotalSessionTimeHud({
  isMobile = false,
  elapsedSec = 0,
  totalSec = 0,
  formatTime,
  style,
}) {
  const fmt = typeof formatTime === "function" ? formatTime : (s) => String(s);

  const safeElapsed = Number.isFinite(Number(elapsedSec)) ? Number(elapsedSec) : 0;
  const safeTotal = Number.isFinite(Number(totalSec)) ? Number(totalSec) : 0;
  const safeRemaining = Math.max(0, safeTotal - safeElapsed);

  const percent =
    safeTotal > 0 ? Math.max(0, Math.min(100, (safeElapsed * 100) / safeTotal)) : 0;

  // Futuristic accent (different from the dark-blue used elsewhere)
  const ACCENT = "rgba(0, 255, 209, 1)"; // neon teal
  const ACCENT_DIM = "rgba(0, 255, 209, 0.22)";
  const ACCENT_LINE = "rgba(0, 255, 209, 0.45)";

  const pad = isMobile ? "8px 10px" : "10px 12px";
  const lineFont = isMobile ? 11 : 12;
  const percentFont = isMobile ? 11 : 12;
  const radius = 14;

  const StatLine = ({ icon, label, value, align = "left" }) => (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: lineFont,
          fontWeight: 900,
          color: "rgba(0,0,0,0.62)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ color: ACCENT }}>{icon}</span>
        <span style={{ letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</span>
        <span style={{ color: "rgba(0,0,0,0.35)" }}>•</span>
        <span style={{ fontWeight: 900, color: "rgba(0,0,0,0.78)" }}>{value}</span>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: "100%",
        borderRadius: radius,
        padding: pad,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.78), rgba(230,255,252,0.68))",
        border: `1px solid ${ACCENT_LINE}`,
        boxShadow: `0 0 0 1px rgba(255,255,255,0.55) inset, 0 10px 26px rgba(0,0,0,0.10), 0 0 22px ${ACCENT_DIM}`,
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      {/* subtle scanline */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
          opacity: 0.55,
        }}
      />

      {/* top row: remaining / elapsed */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <StatLine icon={<ClockCircleOutlined />} label="Elapsed" value={fmt(safeElapsed)} />
        <div
          style={{
            width: 1,
            alignSelf: "stretch",
            background: "rgba(0,0,0,0.08)",
          }}
        />
        <StatLine
          icon={<HourglassOutlined />}
          label="Remaining"
          value={fmt(safeRemaining)}
          align="right"
        />
      </div>

      {/* progress bar */}
      <div style={{ marginTop: isMobile ? 6 : 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              flex: 1,
              height: isMobile ? 8 : 10,
              borderRadius: 999,
              background: "rgba(0,0,0,0.08)",
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${percent}%`,
                background: `linear-gradient(90deg, ${ACCENT}, rgba(124,58,237,0.95))`,
                boxShadow: `0 0 14px ${ACCENT_DIM}`,
                transition: "width 150ms ease",
              }}
            />
          </div>
          <div
            style={{
              width: 44,
              textAlign: "right",
              fontSize: percentFont,
              fontWeight: 900,
              color: "rgba(0,0,0,0.45)",
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}
          >
            {Math.round(percent)}%
          </div>
        </div>
      </div>
    </div>
  );
}


